import { inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import {
  BackendService,
  HttpOptions,
} from '@ipg/ngx-frontend-toolset';
import {
  SearchClientsResponse,
  SearchDevicesResponse,
  UnitResponseModel,
} from '../units.model';

@Injectable({
  providedIn: 'root',
})
export class UnitService {
  private readonly _backend = inject(BackendService);
  private readonly _currentUnit = signal<UnitResponseModel | null>(null);

  public readonly currentUnit = this._currentUnit.asReadonly();

  public getAll(options?: HttpOptions): Observable<UnitResponseModel[]> {
    return this._backend.get(`/units`, options);
  }

  public getByUser(): Observable<UnitResponseModel[]> {
    return this._backend.get(`/units/personalized`);
  }

  public getCurrent(): Observable<UnitResponseModel> {
    return this._backend
      .get<UnitResponseModel>(`/units/current`)
      .pipe(tap((result) => this._currentUnit.set(result)));
  }

  public create(data: unknown, options?: HttpOptions): Observable<unknown> {
    return this._backend.post(`/units`, data, options);
  }

  public delete(unitId: string, options?: HttpOptions): Observable<unknown> {
    return this._backend.delete(`/units/${unitId}`, options);
  }

  public update(
    unitId: string,
    data: unknown,
    options?: HttpOptions
  ): Observable<unknown> {
    return this._backend.put(`/units/${unitId}`, data, options);
  }

  public activate(unitId: string) {
    return this._backend.put(`/units/${unitId}/activate`, null);
  }

  public restore(unitId: string, options?: HttpOptions): Observable<unknown> {
    return this._backend.put(`/units/${unitId}/restore`, null, options);
  }

  public searchDevices(q?: string): Observable<SearchDevicesResponse> {
    const params = q ? { params: { q } } : undefined;
    return this._backend.get<SearchDevicesResponse>('/devices/search', params);
  }

  public searchClients(q?: string): Observable<SearchClientsResponse> {
    const params = q ? { params: { q } } : undefined;
    return this._backend.get<SearchClientsResponse>('/clients/search', params);
  }
}
